import markdownit from 'markdown-it'
import DOMPurify from 'dompurify'
import removeMarkdown from 'remove-markdown'

/**
 * Removes invisible/problematic characters that word processors (e.g. MS Word) inject
 * when text is copy-pasted into a rich text field:
 *
 * - Non-breaking spaces (\u00A0): cause uneven spacing with justified text; replace with normal space
 * - Zero-width spaces (\u200B): invisible break points that cause unexpected line wraps
 * - Zero-width non-joiners/joiners (\u200C, \u200D): affect ligature/joining rendering
 * - Word-style left/right single quotes (\u2018, \u2019): replaced with straight apostrophe
 * - Word-style left/right double quotes (\u201C, \u201D): replaced with straight quotes
 *
 * Soft hyphens (\u00AD) are intentionally kept — they are valid HTML and allow manual
 * hyphenation hints (e.g. "Beteili&shy;gungsplattform").
 */
export const sanitizeRichText = (text: string): string => {
    if (!text) return ''
    return text
        .replace(/\u00A0/g, ' ') // non-breaking space → regular space
        .replace(/\u200B/g, '') // zero-width space → remove
        .replace(/\u200C/g, '') // zero-width non-joiner → remove
        .replace(/\u200D/g, '') // zero-width joiner → remove
        .replace(/[\u2018\u2019]/g, "'") // curly single quotes → apostrophe
        .replace(/[\u201C\u201D]/g, '"') // curly double quotes → quote
}

// Zero preset disables all rules; only explicitly listed rules are active (whitelist approach).
// breaks: true — single \n becomes <br>, matching user expectation in i18n strings and text fields.
const _md = markdownit('zero', { breaks: true }).enable([
    'escape',
    'emphasis',
    'strikethrough',
    'list',
    'newline',
    'paragraph',
    'link',
])

_md.renderer.rules['bullet_list_open'] = () => '<ul class="list-disc">\n'
_md.renderer.rules['ordered_list_open'] = () => '<ol class="list-decimal">\n'

// Keep markdown-it's default link rendering.
// We only adjust a few attributes before the link gets rendered.
const defaultLinkRenderer =
    _md.renderer.rules.link_open ??
    ((tokens, idx, options, _env, self) => self.renderToken(tokens, idx, options))

// Normalizes URLs coming from CMS content.
// Supports links with and without a protocol.
// Domains like domain.de automatically get https://.
const normalizeExternalHttpUrl = (url: string) => {
    const trimmedUrl = url.trim()

    // Handle domains without a protocol, like domain.de or www.domain.de.
    if (/^(?!https?:\/\/)[a-z0-9.-]+\.[a-z]{2,}(\/.*)?$/i.test(trimmedUrl)) {
        return `https://${trimmedUrl}`
    }

    try {
        const parsedUrl = new URL(trimmedUrl)
        return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:' ? trimmedUrl : null
    } catch {
        return null
    }
}

// External links are routed through our redirect page
// so users get a notice before leaving the platform.
const getExternalRedirectUrl = (url: string) => `/external-redirect?link=${encodeURIComponent(url)}`

// Intercept markdown links during rendering.
// External links get routed through our redirect page and receive
// the required attributes and styling.
_md.renderer.rules.link_open = (tokens, id, options, env, self) => {
    const token = tokens[id]
    // Read the href generated by markdown-it.
    const href = token.attrGet('href')
    const externalUrl = href ? normalizeExternalHttpUrl(href) : null

    if (externalUrl) {
        token.attrSet('href', getExternalRedirectUrl(externalUrl))
        token.attrSet('target', '_blank')
        token.attrSet('rel', 'noopener noreferrer')
        token.attrJoin('class', 'text-primary underline hover:text-primary-600')
    }

    return defaultLinkRenderer(tokens, id, options, env, self)
}

// Strip any remaining XSS vectors (e.g. javascript: links) from the rendered HTML.
const purify = (html: string) =>
    DOMPurify.sanitize(html, {
        USE_PROFILES: { html: true },
        ADD_ATTR: ['target', 'rel'],
    })

export const markdown = {
    render: (src: string) => purify(_md.render(sanitizeRichText(src))),
    renderInline: (src: string) => purify(_md.renderInline(sanitizeRichText(src))),
}

export const getMarkdownPlainText = (markdownContent: string) => {
    if (!markdownContent) return ''
    return removeMarkdown(sanitizeRichText(markdownContent))
}

export const getMarkdownPlainTextLength = (markdownContent: string) => {
    return removeMarkdown(sanitizeRichText(markdownContent)).trim().length
}
